// Data Lab · Health & Linkage — read-only data X-ray + the Order↔Person
// linkage sync. Computes live from whatever DB it runs against.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, RefreshCw, Link2, Database, AlertTriangle, CheckCircle2,
  Coins, CalendarClock, UserCog, Sparkles, Loader2, Play,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Panel, Chip } from "./_shared";

const COLL_LINK = {
  persons: "/lab/persons", orders: "/lab/orders", calls: "/lab/calls",
  suppliers: "/lab/suppliers", products: "/lab/products", customers: "/lab/persons",
};

function toneForPct(pct, invert = false) {
  const p = invert ? 100 - pct : pct;
  if (p >= 80) return "green";
  if (p >= 40) return "warn";
  return "red";
}

function Bar({ pct, invert = false }) {
  const tone = toneForPct(pct, invert);
  const color = tone === "green" ? "bg-emerald-500" : tone === "warn" ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
      <div className={`h-full ${color} transition-all`} style={{ width: `${Math.max(2, pct)}%` }} />
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, tone = "slate", testid }) {
  const ring = {
    green: "border-emerald-500/30", warn: "border-amber-500/30",
    red: "border-rose-500/30", slate: "border-white/10",
  }[tone];
  return (
    <div className={`rounded-xl border ${ring} bg-[#111C34] p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-[10px] uppercase tracking-widest font-mono">{label}</span>
      </div>
      <div className="mt-1.5 text-2xl font-bold tabular-nums text-slate-100">{value}</div>
      {sub && <div className="text-[11px] font-mono text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const ALL_STRATEGIES = [
  { key: "order_id", label: "BC order id → OrderIds" },
  { key: "customer_id", label: "BC customer_id → BCCustIds" },
  { key: "email", label: "Billing email → Email" },
];

export default function LabHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [strategies, setStrategies] = useState(["order_id", "customer_id", "email"]);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/datalab/health");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load data health");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [coverage, setCoverage] = useState(null);
  useEffect(() => {
    api.get("/datalab/linkage/coverage").then((r) => setCoverage(r.data)).catch(() => {});
  }, []);

  const toggleStrategy = (k) =>
    setStrategies((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const runLinkage = async () => {
    if (!strategies.length) { toast.error("Pick at least one match strategy"); return; }
    setRunning(true);
    try {
      // SAFETY (descoped): only dry-run previews are permitted while we stabilise
      // data hygiene. Writes are disabled here and guarded on the backend too.
      const { data: res } = await api.post("/datalab/linkage/run", {
        strategies, dry_run: true, relink_all: false,
      });
      setPreview(res);
      toast.success(`Preview: ${res.matched} of ${res.scanned} would link`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Linkage preview failed");
    } finally { setRunning(false); }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-slate-400" data-testid="lab-health-loading">
        <div className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Scanning collections…</div>
      </div>
    );
  }
  if (!data) return null;

  const oq = data.orders_quality || {};
  const pq = data.persons_quality || {};
  const lk = data.linkage || {};
  const rp = lk.resolvable_pct || {};

  return (
    <div className="max-w-[1400px] mx-auto px-5 py-6 space-y-6" data-testid="lab-health-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#C8A46B]" />
            <h1 className="text-xl font-bold text-slate-100">Data Lab · Health & Linkage</h1>
          </div>
          <p className="text-[12px] text-slate-500 mt-1 font-mono">
            Live X-ray of every collection · coverage, data-quality & the order↔person join.
            Sampled to {data.sample_cap?.toLocaleString?.() || data.sample_cap} docs where large.
          </p>
        </div>
        <button onClick={load} data-testid="lab-health-refresh"
          className="inline-flex items-center gap-2 text-[12px] font-mono px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/[0.05]">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* OrderIds ↔ orders coverage (read-only) */}
      {coverage && (
        <Panel icon={Link2} title="OrderIds → Orders coverage" right="read-only">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="lab-orderids-coverage">
            <Metric icon={UserCog} label="Persons with OrderIds"
              value={`${coverage.persons_with_orderids?.toLocaleString?.()} / ${coverage.persons_total?.toLocaleString?.()}`}
              sub={`${coverage.persons_with_orderids_pct}%`} testid="cov-persons" />
            <Metric icon={Link2} label="Numbers resolved"
              value={`${coverage.numbers_resolved?.toLocaleString?.()} / ${coverage.distinct_numbers_referenced?.toLocaleString?.()}`}
              sub={`${coverage.numbers_resolved_pct}% of referenced`} testid="cov-numbers" />
            <Metric icon={Coins} label="Orders claimed"
              value={`${coverage.orders_claimed?.toLocaleString?.()} / ${coverage.orders_total?.toLocaleString?.()}`}
              sub={`${coverage.orders_claimed_pct}% of orders`} testid="cov-orders" />
          </div>
          <p className="text-[11px] text-slate-500 mt-3 font-mono">
            Orders are resolved from each person's OrderIds via BCOrder._id / BCOrder.id. Pure read — no writes.
          </p>
        </Panel>
      )}

      {/* Linkage hero */}
      <Panel icon={Link2} title="Order ↔ Person Linkage" right={`${lk.orders_total?.toLocaleString?.() || 0} orders`}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric icon={Link2} label="Linked now" value={`${oq.linked_now_pct ?? 0}%`}
            tone={toneForPct(oq.linked_now_pct ?? 0)} sub={`target ${lk.target_pct}%`} testid="lab-linkage-now" />
          <Metric icon={CheckCircle2} label="Resolvable (any)" value={`${rp.any ?? 0}%`}
            tone={toneForPct(rp.any ?? 0)} sub="if we run linkage" testid="lab-linkage-resolvable" />
          <Metric icon={Database} label="By customer_id" value={`${rp.customer_id ?? 0}%`} sub="BCCustIds match" />
          <Metric icon={Database} label="By email" value={`${rp.email ?? 0}%`} sub="billing → Email" />
        </div>
        {(rp.any ?? 0) < 20 && (
          <div className="mt-3 flex items-start gap-2 text-[12px] text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Very low resolvability. On the seed/test DB this is expected (orders &amp; persons were seeded independently). If live shows the same, the fix is upstream in the sync key mapping — not in the app.</span>
          </div>
        )}

        {/* Linkage runner (admin) */}
        <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4" data-testid="lab-linkage-runner">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono mb-2">Run linkage sync</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {ALL_STRATEGIES.map((s, i) => (
              <button key={s.key} onClick={() => toggleStrategy(s.key)}
                data-testid={`lab-strategy-${s.key}`}
                className={`text-[11px] font-mono px-2.5 py-1.5 rounded-lg border transition-colors ${
                  strategies.includes(s.key)
                    ? "border-teal-500/40 bg-teal-500/15 text-teal-200"
                    : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
                {strategies.includes(s.key) ? `${strategies.indexOf(s.key) + 1}. ` : ""}{s.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => runLinkage()} disabled={running}
              data-testid="lab-linkage-preview-btn"
              className="inline-flex items-center gap-2 text-[12px] font-mono px-3 py-2 rounded-lg border border-white/15 text-slate-200 hover:bg-white/5 disabled:opacity-50">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Preview (dry run)
            </button>
            {preview && (
              <span className="text-[12px] font-mono text-slate-400">
                Preview: matched <b className="text-slate-100">{preview.matched}</b> / {preview.scanned}
                {" · "}
                {Object.entries(preview.by_strategy || {}).map(([k, v]) => `${k}:${v}`).join("  ")}
              </span>
            )}
          </div>
          <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-lg p-3" data-testid="lab-linkage-writes-disabled">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span><b>Writing links is temporarily disabled</b> (dry-run preview only) while we stabilise data hygiene. Preview computes counts without changing any data. Re-enable is a one-line change once we're confident in the match keys.</span>
          </div>
        </div>
      </Panel>

      {/* Data quality */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel icon={Coins} title="Orders · data quality">
          <div className="grid grid-cols-2 gap-3">
            <Metric icon={Coins} label="Money as £-string" value={`${oq.money_string_pct ?? 0}%`}
              tone={toneForPct(oq.money_string_pct ?? 0, true)} sub="needs numeric normalise" testid="lab-oq-money" />
            <Metric icon={CheckCircle2} label="Xero numeric money" value={`${oq.money_numeric_xero_pct ?? 0}%`}
              tone={toneForPct(oq.money_numeric_xero_pct ?? 0)} sub="clean source" />
            <Metric icon={CalendarClock} label="ISO dates" value={`${oq.iso_date_pct ?? 0}%`}
              tone={toneForPct(oq.iso_date_pct ?? 0)} sub={`${oq.non_iso_date_pct ?? 0}% non-ISO`} testid="lab-oq-dates" />
            <Metric icon={Database} label="Sampled" value={(oq.sample ?? 0).toLocaleString()} sub="orders scanned" />
          </div>
        </Panel>
        <Panel icon={UserCog} title="Persons · ownership & enrichment">
          <div className="grid grid-cols-2 gap-3">
            <Metric icon={UserCog} label="Has an owner (AM)" value={`${pq.owner_pct ?? 0}%`}
              tone={toneForPct(pq.owner_pct ?? 0)} sub="PrimaryAM / AccountManager" testid="lab-pq-owner" />
            <Metric icon={Sparkles} label="Enriched" value={`${pq.enrichment_pct ?? 0}%`}
              tone={toneForPct(pq.enrichment_pct ?? 0)} sub="company/credit/web" testid="lab-pq-enrich" />
            <Metric icon={AlertTriangle} label="Roll-up mismatch" value={`${pq.rollup_mismatch_pct ?? 0}%`}
              tone={toneForPct(pq.rollup_mismatch_pct ?? 0, true)} sub="orders count ≠ OrderIds" testid="lab-pq-rollup" />
            <Metric icon={Database} label="Sampled" value={(pq.sample ?? 0).toLocaleString()} sub="persons scanned" />
          </div>
        </Panel>
      </div>

      {/* Per-collection coverage */}
      <Panel icon={Database} title="Collections · field coverage" right={`${(data.collections || []).length} collections`}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lab-collections">
          {(data.collections || []).map((c) => (
            <div key={c.name} className="rounded-xl border border-white/[0.08] bg-black/20 p-3.5" data-testid={`lab-coll-${c.name}`}>
              <div className="flex items-center justify-between mb-2">
                <Link to={COLL_LINK[c.name] || "#"} className="text-[13px] font-semibold text-slate-100 hover:text-[#C8A46B]">
                  {c.label}
                </Link>
                <Chip tone="slate">{(c.count ?? 0).toLocaleString()} docs</Chip>
              </div>
              <div className="space-y-1.5 max-h-64 overflow-auto pr-1">
                {(c.fields || []).slice(0, 100).map((f) => (
                  <div key={f.key} className="text-[11px] font-mono">
                    <div className="flex items-center justify-between text-slate-400">
                      <span className="truncate mr-2">{f.key}</span>
                      <span className="tabular-nums text-slate-500">{f.pct}%</span>
                    </div>
                    <Bar pct={f.pct} />
                  </div>
                ))}
                {(!c.fields || c.fields.length === 0) && <div className="text-[11px] text-slate-600">No fields sampled.</div>}
              </div>
              <div className="text-[10px] text-slate-600 font-mono mt-2">sampled {c.sample?.toLocaleString?.() || c.sample} docs</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
